import React from 'react';

import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type SummaryCardColor = 'blue' | 'green' | 'yellow' | 'red' | 'grey';

interface SummaryCardProps {
  testId?: string;
  /** Custom React node icon (e.g., SparklesIcon). Use this OR faIcon, not both. You control all styling. */
  customIcon?: React.ReactNode;
  /** FontAwesome icon definition. SummaryCard will handle coloring based on isSelected state. */
  faIcon?: IconDefinition;
  value: string | number;
  label: string;
  description: string;
  color: SummaryCardColor;
  isSelected?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}

const getSelectedBorderClass = (color: string) => {
  if (color === 'blue') return 'vai-border-blue-600';
  if (color === 'green') return 'vai-border-green-600';
  if (color === 'yellow') return 'vai-border-amber-500';
  if (color === 'red') return 'vai-border-red-600';
  if (color === 'grey') return 'vai-border-gray-500';
  return 'vai-border-blue-600';
};

const getValueColorClass = (color: string) => {
  if (color === 'blue') return 'vai-text-blue-600';
  if (color === 'green') return 'vai-text-green-600';
  if (color === 'yellow') return 'vai-text-amber-600';
  if (color === 'red') return 'vai-text-red-600';
  if (color === 'grey') return 'vai-text-gray-600';
  return 'vai-text-blue-600';
};

const getIconColorClass = (color: SummaryCardColor, isSelected: boolean) => {
  if (isSelected) {
    if (color === 'blue') return 'vai-text-blue-600';
    if (color === 'green') return 'vai-text-green-600';
    if (color === 'yellow') return 'vai-text-amber-500';
    if (color === 'red') return 'vai-text-red-600';
    if (color === 'grey') return 'vai-text-gray-600';
    return 'vai-text-blue-600';
  }
  // Specific colors/opacities when not selected
  if (color === 'blue') return 'vai-text-blue-600/20';
  if (color === 'green') return 'vai-text-green-600/20'; // Green 600 at 20%
  if (color === 'yellow') return 'vai-text-amber-500/[0.33]'; // Amber 500 at 33%
  if (color === 'red') return 'vai-text-red-100'; // Red 100
  if (color === 'grey') return 'vai-text-zinc-300'; // Zinc 300
  return 'vai-text-blue-600/20';
};

const getLabelColorClass = (color: string, isSelected: boolean) => {
  if (!isSelected) return 'vai-text-zinc-500';
  if (color === 'blue') return 'vai-text-blue-600';
  if (color === 'green') return 'vai-text-green-600';
  if (color === 'yellow') return 'vai-text-amber-600';
  if (color === 'red') return 'vai-text-red-600';
  if (color === 'grey') return 'vai-text-gray-600';
  return 'vai-text-blue-600';
};

const getCardClasses = (isClickable: boolean) => {
  const baseClasses =
    'vai-flex vai-flex-col vai-gap-2 vai-rounded-xl vai-w-full vai-border vai-h-full vai-shadow-sm';
  const interactiveClasses = isClickable
    ? 'vai-cursor-pointer transition-all duration-200 hover:vai-shadow-md'
    : '';
  return `${baseClasses} ${interactiveClasses}`;
};

export const SummaryCard = React.memo(
  ({
    testId = 'summary-card',
    customIcon,
    faIcon,
    value,
    label,
    description,
    color,
    isSelected,
    isLoading,
    onClick,
  }: SummaryCardProps) => {
    const isClickable = !!onClick;

    // Render the appropriate icon based on props
    const renderIcon = () => {
      if (isLoading) {
        return (
          <FontAwesomeIcon
            icon={faSpinner}
            className="vai-text-blue-500 vai-animate-spin"
          />
        );
      }

      // If faIcon is provided, render FontAwesome icon with automatic coloring
      if (faIcon) {
        return (
          <FontAwesomeIcon
            icon={faIcon}
            className={`vai-w-6 vai-h-6 ${getIconColorClass(
              color,
              isSelected || false,
            )}`}
          />
        );
      }

      // For custom icons, apply opacity when clickable and not selected
      if (customIcon && isClickable && !isSelected) {
        return <div className="vai-opacity-30">{customIcon}</div>;
      }

      // Otherwise render the custom icon as-is
      return customIcon;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        onClick();
      }
    };

    return (
      <div
        // Includes 1px default border
        className={getCardClasses(isClickable)}
        data-testid={testId}
      >
        <div
          // Props to make card clickable and accessible (only if onClick provided)
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onClick={onClick}
          onKeyDown={isClickable ? handleKeyDown : undefined}
          // Adds 1px colored border when selected
          className={`vai-flex vai-flex-col vai-rounded-xl vai-px-8 vai-py-6 vai-bg-white vai-border vai-h-full ${
            isSelected
              ? getSelectedBorderClass(color)
              : 'vai-border-transparent'
          }`}
          data-testid={`${testId}-button`}
        >
          <div className="vai-flex vai-justify-between">
            <div className="vai-flex vai-flex-col vai-gap-1">
              <span
                className={`vai-text-base vai-font-medium vai-uppercase vai-h-6 vai-tracking-[0.03em] ${getLabelColorClass(
                  color,
                  isSelected || false,
                )}`}
                data-testid={`${testId}-label`}
              >
                {label}
              </span>
              <div className="vai-flex vai-items-center vai-gap-4 vai-py-1">
                <span
                  className={`vai-text-6xl vai-font-bold ${getValueColorClass(
                    color,
                  )}`}
                  data-testid={`${testId}-value`}
                >
                  {value}
                </span>
                <span
                  className="vai-text-base vai-text-zinc-900 vai-max-w-52"
                  data-testid={`${testId}-description`}
                >
                  {description}
                </span>
              </div>
            </div>
            <div
              className="vai-w-6 vai-h-6 vai-flex vai-items-center vai-justify-center"
              data-testid={`${testId}-icon`}
            >
              {renderIcon()}
            </div>
          </div>
        </div>
      </div>
    );
  },
);
